import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useAdminTheme } from '../../context/AdminThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Plus, Edit, Trash2, CheckCircle, XCircle, 
  Sun, Moon, Lightbulb, Grid, Wrench
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api`;

const COURT_STATUSES = [
  { value: 'active', label: 'Active', color: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircle },
  { value: 'inactive', label: 'Inactive', color: 'bg-rose-500/20 text-rose-400', icon: XCircle },
  { value: 'maintenance', label: 'Under Maintenance', color: 'bg-amber-500/20 text-amber-400', icon: Wrench }
];

const AdminCourtsPage = () => {
  const { token, hasRole } = useAdminAuth();
  const { isDarkMode, theme } = useAdminTheme();
  const [courts, setCourts] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [blockOffs, setBlockOffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCourtModal, setShowCourtModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [editingCourt, setEditingCourt] = useState(null);
  const [editingPricing, setEditingPricing] = useState(null);
  const [courtForm, setCourtForm] = useState({
    name: '', 
    court_type: 'Singles/Doubles', 
    surface: 'Pro Cushion', 
    indoor: true, 
    lighting: true,
    status: 'active'
  });
  const [pricingForm, setPricingForm] = useState({
    name: '', 
    court_selection: 'all',
    selected_courts: [],
    price_per_hour: 500, 
    day_type: 'weekday', 
    start_time: '06:00',
    end_time: '22:00'
  });
  const [blockForm, setBlockForm] = useState({
    court_id: '', date: '', start_time: '', end_time: '', reason: ''
  });

  const resetPricingForm = () => {
    setPricingForm({
      name: '', 
      court_selection: 'all',
      selected_courts: [],
      price_per_hour: 500, 
      day_type: 'weekday', 
      start_time: '06:00',
      end_time: '22:00'
    });
    setEditingPricing(null);
  };

  const openEditPricingModal = (rule) => {
    let courtSelection = 'all';
    let selectedCourts = [];
    
    if (rule.court_id !== 'all') {
      courtSelection = 'selected';
      if (rule.selected_courts && rule.selected_courts.length > 0) {
        selectedCourts = rule.selected_courts;
      } else if (rule.court_id.includes(',')) {
        selectedCourts = rule.court_id.split(',');
      } else {
        selectedCourts = [rule.court_id];
      }
    }
    
    setPricingForm({
      name: rule.name || '',
      court_selection: courtSelection,
      selected_courts: selectedCourts,
      price_per_hour: rule.price_per_hour || 500,
      day_type: rule.day_type || 'weekday',
      start_time: rule.start_time || '06:00',
      end_time: rule.end_time || '22:00'
    });
    setEditingPricing(rule);
    setShowPricingModal(true);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [courtsRes, pricingRes, blocksRes] = await Promise.all([
        fetch(`${API}/admin/courts`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/admin/pricing`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/admin/block-offs`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (courtsRes.ok) setCourts(await courtsRes.json());
      if (pricingRes.ok) setPricing(await pricingRes.json());
      if (blocksRes.ok) setBlockOffs(await blocksRes.json());
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const saveCourt = async () => {
    try {
      const url = editingCourt 
        ? `${API}/admin/courts/${editingCourt.court_id}`
        : `${API}/admin/courts`;
      
      await fetch(url, {
        method: editingCourt ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(courtForm)
      });
      
      toast.success(editingCourt ? 'Court updated' : 'Court created');
      setShowCourtModal(false);
      setEditingCourt(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to save court');
    }
  };

  const deleteCourt = async (courtId) => {
    if (!window.confirm('Delete this court?')) return;
    try {
      await fetch(`${API}/admin/courts/${courtId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Court deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete court');
    }
  };

  const savePricing = async () => {
    try {
      const url = editingPricing 
        ? `${API}/admin/pricing/${editingPricing.rule_id}`
        : `${API}/admin/pricing`;
      
      const courtId = pricingForm.court_selection === 'all' 
        ? 'all' 
        : pricingForm.selected_courts.join(',');
      
      await fetch(url, {
        method: editingPricing ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...pricingForm,
          court_id: courtId,
          selected_courts: pricingForm.court_selection === 'selected' ? pricingForm.selected_courts : []
        })
      });
      
      toast.success(editingPricing ? 'Pricing rule updated' : 'Pricing rule created');
      setShowPricingModal(false);
      resetPricingForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save pricing rule');
    }
  };

  const deletePricing = async (ruleId) => {
    try {
      await fetch(`${API}/admin/pricing/${ruleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Pricing rule deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete pricing rule');
    }
  };

  const saveBlockOff = async () => {
    try {
      await fetch(`${API}/admin/block-offs`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(blockForm)
      });
      toast.success('Court blocked');
      setShowBlockModal(false);
      setBlockForm({ court_id: '', date: '', start_time: '', end_time: '', reason: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to block court');
    }
  };

  const deleteBlockOff = async (blockId) => {
    try {
      await fetch(`${API}/admin/block-offs/${blockId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Block off removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove block off');
    }
  };

  const getStatusInfo = (status) => {
    return COURT_STATUSES.find(s => s.value === status) || COURT_STATUSES[0];
  };

  const formatTimeRange = (rule) => {
    if (rule.start_time && rule.end_time) {
      return `${rule.start_time} - ${rule.end_time}`;
    }
    return rule.time_type || 'All Day';
  };

  // Dynamic classes based on theme
  const inputClass = `w-full h-10 px-3 rounded-md border ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`;
  const selectClass = `w-full h-10 px-3 rounded-md border ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`;
  const cardBgClass = isDarkMode ? 'bg-slate-700/30' : 'bg-gray-100';

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${theme.heading}`}>Court Management</h1>
            <p className={`${theme.textMuted} mt-1`}>Manage courts, pricing, and availability</p>
          </div>
          {hasRole(['super_admin', 'manager']) && (
            <Button onClick={() => setShowCourtModal(true)} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="h-4 w-4 mr-2" /> Add Court
            </Button>
          )}
        </div>

        {/* Courts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {loading ? (
            <div className="col-span-full text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto"></div>
            </div>
          ) : courts.length === 0 ? (
            <p className={`col-span-full ${theme.textMuted} text-center py-12`}>No courts found</p>
          ) : (
            courts.map((court) => {
              const statusInfo = getStatusInfo(court.status);
              const StatusIcon = statusInfo.icon;
              
              return (
                <Card key={court.court_id} className={theme.card}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className={`font-medium ${theme.text}`}>{court.name}</h3>
                        <p className={`text-sm ${theme.textMuted}`}>{court.court_type}</p>
                      </div>
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${statusInfo.color}`}>
                        <StatusIcon className="h-3 w-3" />
                        {statusInfo.label}
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${isDarkMode ? 'bg-slate-700/50 text-slate-300' : 'bg-gray-200 text-gray-700'}`}>
                        <Grid className="h-3 w-3" /> {court.surface}
                      </span>
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${court.indoor ? 'bg-blue-500/20 text-blue-400' : 'bg-amber-500/20 text-amber-400'}`}>
                        {court.indoor ? <Moon className="h-3 w-3" /> : <Sun className="h-3 w-3" />}
                        {court.indoor ? 'Indoor' : 'Outdoor'}
                      </span>
                      {court.lighting && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-500/20 text-amber-400 rounded text-xs">
                          <Lightbulb className="h-3 w-3" /> Lighting
                        </span>
                      )}
                    </div>

                    {hasRole(['super_admin', 'manager']) && (
                      <div className={`flex gap-2 pt-2 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                        <Button
                          size="sm"
                          variant="ghost"
                          className={`flex-1 ${theme.textMuted}`}
                          onClick={() => {
                            setEditingCourt(court);
                            setCourtForm({
                              name: court.name || '',
                              court_type: court.court_type || 'Singles/Doubles',
                              surface: court.surface || 'Pro Cushion',
                              indoor: court.indoor !== false,
                              lighting: court.lighting !== false,
                              status: court.status || 'active'
                            });
                            setShowCourtModal(true);
                          }}
                        >
                          <Edit className="h-4 w-4 mr-1" /> Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-rose-400"
                          onClick={() => deleteCourt(court.court_id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Pricing Rules */}
        <Card className={theme.card}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className={theme.heading}>Pricing Rules</CardTitle>
            {hasRole(['super_admin', 'manager']) && (
              <Button size="sm" onClick={() => { resetPricingForm(); setShowPricingModal(true); }} className="bg-emerald-600">
                <Plus className="h-4 w-4 mr-1" /> Add Rule
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {pricing.length === 0 ? (
              <p className={`${theme.textMuted} text-center py-4`}>No custom pricing rules</p>
            ) : (
              <div className="space-y-2">
                {pricing.map((rule) => {
                  let courtDisplay = 'All Courts';
                  if (rule.court_id !== 'all') {
                    if (rule.selected_courts && rule.selected_courts.length > 0) {
                      const courtNames = rule.selected_courts.map(id => 
                        courts.find(c => c.court_id === id)?.name || id
                      );
                      courtDisplay = courtNames.length > 2 
                        ? `${courtNames.slice(0, 2).join(', ')} +${courtNames.length - 2} more`
                        : courtNames.join(', ');
                    } else if (rule.court_id.includes(',')) {
                      const ids = rule.court_id.split(',');
                      const courtNames = ids.map(id => 
                        courts.find(c => c.court_id === id)?.name || id
                      );
                      courtDisplay = courtNames.length > 2 
                        ? `${courtNames.slice(0, 2).join(', ')} +${courtNames.length - 2} more`
                        : courtNames.join(', ');
                    } else {
                      courtDisplay = courts.find(c => c.court_id === rule.court_id)?.name || rule.court_id;
                    }
                  }
                  
                  return (
                    <div key={rule.rule_id} className={`flex items-center justify-between p-3 rounded ${cardBgClass}`}>
                      <div>
                        <p className={`${theme.text} font-medium`}>{rule.name}</p>
                        <p className={`text-sm ${theme.textMuted}`}>
                          {courtDisplay} • {rule.day_type} • {formatTimeRange(rule)}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <p className="text-emerald-500 font-medium">₱{rule.price_per_hour}/hr</p>
                        {hasRole(['super_admin', 'manager']) && (
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" className={theme.textMuted} onClick={() => openEditPricingModal(rule)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-rose-400" onClick={() => deletePricing(rule.rule_id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Block Offs */}
        <Card className={theme.card}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className={theme.heading}>Court Block Offs</CardTitle>
            {hasRole(['super_admin', 'manager', 'supervisor']) && (
              <Button size="sm" onClick={() => setShowBlockModal(true)} className="bg-emerald-600">
                <Plus className="h-4 w-4 mr-1" /> Block Court
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {blockOffs.length === 0 ? (
              <p className={`${theme.textMuted} text-center py-4`}>No block offs scheduled</p>
            ) : (
              <div className="space-y-2">
                {blockOffs.map((block) => (
                  <div key={block.block_id} className={`flex items-center justify-between p-3 rounded ${cardBgClass}`}>
                    <div>
                      <p className={`${theme.text} font-medium`}>{block.court_id?.replace('court_', 'Court ')}</p>
                      <p className={`text-sm ${theme.textMuted}`}>
                        {block.date} • {block.start_time} - {block.end_time}
                      </p>
                      <p className="text-xs text-rose-400">{block.reason}</p>
                    </div>
                    <Button size="sm" variant="ghost" className="text-rose-400" onClick={() => deleteBlockOff(block.block_id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Court Modal */}
        {showCourtModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className={`w-full max-w-md ${theme.card}`}>
              <CardHeader>
                <CardTitle className={theme.heading}>{editingCourt ? 'Edit Court' : 'Add Court'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className={theme.text}>Court Name</Label>
                  <Input
                    value={courtForm.name}
                    onChange={(e) => setCourtForm(prev => ({ ...prev, name: e.target.value }))}
                    className={inputClass}
                    placeholder="Court 1"
                  />
                </div>
                <div>
                  <Label className={theme.text}>Court Type</Label>
                  <select
                    value={courtForm.court_type}
                    onChange={(e) => setCourtForm(prev => ({ ...prev, court_type: e.target.value }))}
                    className={selectClass}
                  >
                    <option>Singles/Doubles</option>
                    <option>Tournament</option>
                    <option>Practice</option>
                  </select>
                </div>
                <div>
                  <Label className={theme.text}>Surface</Label>
                  <select
                    value={courtForm.surface}
                    onChange={(e) => setCourtForm(prev => ({ ...prev, surface: e.target.value }))}
                    className={selectClass}
                  >
                    <option>Pro Cushion</option>
                    <option>Premium Cushion</option>
                    <option>Standard</option>
                  </select>
                </div>
                
                <div>
                  <Label className={theme.text}>Court Status</Label>
                  <select
                    value={courtForm.status}
                    onChange={(e) => setCourtForm(prev => ({ ...prev, status: e.target.value }))}
                    className={selectClass}
                  >
                    {COURT_STATUSES.map(status => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                </div>
                
                <div className="flex gap-4">
                  <label className={`flex items-center gap-2 ${theme.text}`}>
                    <input
                      type="checkbox"
                      checked={courtForm.indoor}
                      onChange={(e) => setCourtForm(prev => ({ ...prev, indoor: e.target.checked }))}
                      className="rounded"
                    />
                    Indoor
                  </label>
                  <label className={`flex items-center gap-2 ${theme.text}`}>
                    <input
                      type="checkbox"
                      checked={courtForm.lighting}
                      onChange={(e) => setCourtForm(prev => ({ ...prev, lighting: e.target.checked }))}
                      className="rounded"
                    />
                    Lighting
                  </label>
                </div>
                <div className="flex gap-2 pt-4">
                  <Button 
                    variant="outline" 
                    className={`flex-1 ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`} 
                    onClick={() => { setShowCourtModal(false); setEditingCourt(null); }}
                  >
                    Cancel
                  </Button>
                  <Button className="flex-1 bg-emerald-600" onClick={saveCourt}>
                    {editingCourt ? 'Update' : 'Create'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Pricing Modal */}
        {showPricingModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <Card className={`w-full max-w-md my-8 ${theme.card}`}>
              <CardHeader>
                <CardTitle className={theme.heading}>{editingPricing ? 'Edit Pricing Rule' : 'Add Pricing Rule'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className={theme.text}>Rule Name</Label>
                  <Input
                    value={pricingForm.name}
                    onChange={(e) => setPricingForm(prev => ({ ...prev, name: e.target.value }))}
                    className={inputClass}
                    placeholder="Weekend Peak Hours"
                  />
                </div>
                
                <div>
                  <Label className={theme.text}>Apply To</Label>
                  <select
                    value={pricingForm.court_selection}
                    onChange={(e) => setPricingForm(prev => ({ 
                      ...prev, 
                      court_selection: e.target.value,
                      selected_courts: e.target.value === 'all' ? [] : prev.selected_courts
                    }))}
                    className={selectClass}
                  >
                    <option value="all">All Courts</option>
                    <option value="selected">Selected Courts</option>
                  </select>
                </div>

                {pricingForm.court_selection === 'selected' && (
                  <div>
                    <Label className={`${theme.text} mb-2 block`}>Select Courts</Label>
                    <div className={`border rounded-md p-3 max-h-48 overflow-y-auto space-y-2 ${isDarkMode ? 'bg-slate-900/50 border-slate-600' : 'bg-gray-50 border-gray-300'}`}>
                      {courts.map((court) => {
                        const statusInfo = getStatusInfo(court.status);
                        return (
                          <label 
                            key={court.court_id} 
                            className={`flex items-center gap-3 p-2 rounded cursor-pointer ${isDarkMode ? 'hover:bg-slate-700/50' : 'hover:bg-gray-100'}`}
                          >
                            <input
                              type="checkbox"
                              checked={pricingForm.selected_courts.includes(court.court_id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setPricingForm(prev => ({
                                    ...prev,
                                    selected_courts: [...prev.selected_courts, court.court_id]
                                  }));
                                } else {
                                  setPricingForm(prev => ({
                                    ...prev,
                                    selected_courts: prev.selected_courts.filter(id => id !== court.court_id)
                                  }));
                                }
                              }}
                              className="rounded border-slate-500 text-emerald-500 focus:ring-emerald-500"
                            />
                            <div className="flex-1">
                              <span className={theme.text}>{court.name}</span>
                              <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${statusInfo.color}`}>
                                {statusInfo.label}
                              </span>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    {pricingForm.selected_courts.length > 0 && (
                      <p className={`text-xs ${theme.textMuted} mt-2`}>
                        {pricingForm.selected_courts.length} court(s) selected
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <Label className={theme.text}>Price per Hour (₱)</Label>
                  <Input
                    type="number"
                    value={pricingForm.price_per_hour}
                    onChange={(e) => setPricingForm(prev => ({ ...prev, price_per_hour: parseFloat(e.target.value) }))}
                    className={inputClass}
                  />
                </div>
                <div>
                  <Label className={theme.text}>Day Type</Label>
                  <select
                    value={pricingForm.day_type}
                    onChange={(e) => setPricingForm(prev => ({ ...prev, day_type: e.target.value }))}
                    className={selectClass}
                  >
                    <option value="weekday">Weekday</option>
                    <option value="weekend">Weekend</option>
                    <option value="holiday">Holiday</option>
                    <option value="all">All Days</option>
                  </select>
                </div>
                
                <div>
                  <Label className={theme.text}>Effective Hours</Label>
                  <div className="grid grid-cols-2 gap-4 mt-1">
                    <div>
                      <Label className={`${theme.textMuted} text-xs`}>Start Time</Label>
                      <Input
                        type="time"
                        value={pricingForm.start_time}
                        onChange={(e) => setPricingForm(prev => ({ ...prev, start_time: e.target.value }))}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <Label className={`${theme.textMuted} text-xs`}>End Time</Label>
                      <Input
                        type="time"
                        value={pricingForm.end_time}
                        onChange={(e) => setPricingForm(prev => ({ ...prev, end_time: e.target.value }))}
                        className={inputClass}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2 pt-4">
                  <Button 
                    variant="outline" 
                    className={`flex-1 ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`} 
                    onClick={() => { setShowPricingModal(false); resetPricingForm(); }}
                  >
                    Cancel
                  </Button>
                  <Button className="flex-1 bg-emerald-600" onClick={savePricing}>
                    {editingPricing ? 'Update' : 'Create'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Block Off Modal */}
        {showBlockModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className={`w-full max-w-md ${theme.card}`}>
              <CardHeader>
                <CardTitle className={theme.heading}>Block Court</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className={theme.text}>Court</Label>
                  <select
                    value={blockForm.court_id}
                    onChange={(e) => setBlockForm(prev => ({ ...prev, court_id: e.target.value }))}
                    className={selectClass}
                  >
                    <option value="">Select Court</option>
                    {courts.map(c => <option key={c.court_id} value={c.court_id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label className={theme.text}>Date</Label>
                  <Input
                    type="date"
                    value={blockForm.date}
                    onChange={(e) => setBlockForm(prev => ({ ...prev, date: e.target.value }))}
                    className={inputClass}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={theme.text}>Start Time</Label>
                    <Input
                      type="time"
                      value={blockForm.start_time}
                      onChange={(e) => setBlockForm(prev => ({ ...prev, start_time: e.target.value }))}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <Label className={theme.text}>End Time</Label>
                    <Input
                      type="time"
                      value={blockForm.end_time}
                      onChange={(e) => setBlockForm(prev => ({ ...prev, end_time: e.target.value }))}
                      className={inputClass}
                    />
                  </div>
                </div>
                <div>
                  <Label className={theme.text}>Reason</Label>
                  <Input
                    value={blockForm.reason}
                    onChange={(e) => setBlockForm(prev => ({ ...prev, reason: e.target.value }))}
                    className={inputClass}
                    placeholder="Maintenance, Private Event, etc."
                  />
                </div>
                <div className="flex gap-2 pt-4">
                  <Button 
                    variant="outline" 
                    className={`flex-1 ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`} 
                    onClick={() => setShowBlockModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button className="flex-1 bg-emerald-600" onClick={saveBlockOff}>
                    Block
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminCourtsPage;
